import React, { useEffect } from 'react';
import { Typography, withStyles } from '@material-ui/core';
import { del, get, handleResponse, patch, post, swal500 } from '../../../utils/network';
import {
  FormLayout,
  TextInput,
  useFormContainer,
  useFormValidation,
} from '../../common';
import validateDocumentType from './validator';
import Swal from 'sweetalert2';

import styles from './styles';
import { ENTITY_PROPS, MESSAGES } from '../DocumentTable';
import JsonEditorContainer from '../../JsonEditor/JsonEditorContainer';
import { toast, ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../../utils/config';
import { useTheme } from '@material-ui/core';

const FIELDS = {
  documentType: 'Tipo de documento',
  description: 'Descripción',
  code: 'Codigo',
  alias: "Alias",
  schema: 'Esquema',
  identifier: 'Identificador',
  aggregator: 'Agrupador'
};

const INITIAL_STATE = {
  documentType: '',
  description: '',
  code: '',
  alias: '',
  schema: '',
  identifier: '',
  aggregator: '',
};

const DocumentTypeForm = ({ match, history, classes }) => {
  const { id } = match.params;
  const { entityName, endpoint, path } = ENTITY_PROPS;
  const theme = useTheme();

  const { handleSubmit, handleChange, handleBlur, values, errors, setErrors, resetValues } =
    useFormValidation(INITIAL_STATE, values => validateDocumentType(values));

  const {
    data,
    setData,
    editMode,
    setEditMode,
    duplicate,
    loading,
    setLoading,
    submitting,
    setSubmitting,
    isNew,
    handleClose,
    handleCancel,
  } = useFormContainer(ENTITY_PROPS, resetValues, MESSAGES.deleteConfirmation(values));

  const getChangedValues = values => {
    const changedValues = [];
    Object.keys(data).forEach(key => data[key] !== values[key] && changedValues.push(key));
    return changedValues;
  };

  useEffect(() => {
    const getDocumentType = () => {
      setLoading(true);
      if (id !== 'new') {
        get(`${endpoint}/${id}`)
          .then(res => handleResponse(res, { history }))
          .then(resDocumentType => {
            setData(resDocumentType.message);
            resetValues(resDocumentType.message);
          })
          .catch(err => {
            swal500(err);
            history.push(path);
          })
          .finally(() => setLoading(false));
      } else if(duplicate) {
        get(`${endpoint}/${duplicate}`)
          .then(res => handleResponse(res, { history }))
          .then(resDocumentType => {
            setData(resDocumentType.message);
            resetValues(resDocumentType.message);
          })
          .catch(err => {
            swal500(err);
            history.push(path);
          })
          .finally(() => {
            setLoading(false);
            setEditMode(true);
          });
      } else {
        setLoading(false);
        setEditMode(true);
      }
    };

    getDocumentType();
  }, [id, duplicate, endpoint, history, path, resetValues, setData, setEditMode, setLoading]);

  const hasChanges = () => editMode && getChangedValues(values).length > 0;

  const handleResult = e => {
    const formValid = handleSubmit(e);
    if (formValid) {
      setSubmitting(true);
      const requestBody = {
        documentType: values.documentType,
        description: values.description,
        code: values.code,
        alias: values.alias,
        schema: values.schema,
        identifier: values.identifier,
        aggregator: values.aggregator,
      };
      const handle400AsToast = err => {
        err.text().then(error => {
          const errorObject = JSON.parse(error);
          errorObject.fields.forEach(field => {
            toast.error(`${FIELDS[field.name] ?? field.name}: ${field.message}`, {
              position: 'top-center',
              autoClose: 4000,
              hideProgressBar: true,
              closeOnClick: true,
              transition: Slide,
            });
          });
        });
        return Promise.reject(400);
      };

      if (isNew) {
        post(endpoint, requestBody)
          .then(res =>
            handleResponse(res, { history }, [
              { status: 400, method: handle400AsToast },
            ])
          )
          .then(() => history.push({ pathname: path, state: { msg: 'created' } }))
          .catch(err => swal500(err))
          .finally(() => setSubmitting(false));
      } else {
        patch(`${endpoint}/${id}`, requestBody)
          .then(res =>
            handleResponse(res, { history }, [
              { status: 400, method: handle400AsToast },
            ])
          )
          .then(() => history.push({ pathname: path, state: { msg: 'edited' } }))
          .catch(err =>  swal500(err))
          .finally(() => setSubmitting(false));
      }
    } else {
      setErrors({});
      if (!values.documentType) {
        toast.error('Por favor, complete el tipo de documento', {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          transition: Slide,
        });
      }
    }
  };

  const handleSchemaChange = updatedSchema => {
    handleChange({
      target: { name: 'schema', value: updatedSchema }
    });
  };

  const handleCopyCurl = () => {
    if (data && data.schema && data.documentType ){

      const jsonData = JSON.parse(data.schema).properties
      let cUrl = `curl -X POST -H "Authorization: Bearer <<token>>" ${config.backEndUrl}documents -F "document=@<<archivo>>" -F "type=${data.documentType}" `


      for (const [key, value] of Object.entries(jsonData)) {
        if (key !== 'type') {
          cUrl += ` -F "${key}=<<metadata>>" `
        }
      }
      navigator.clipboard.writeText(cUrl)

      toast.success('cUrl copiado al portapapeles!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        transition: Slide,
      });
    } else {
      toast.error('No se pudo copiar el cUrl', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        transition: Slide,
      });
    }
  }

  const handleDelete = id => {
      const item = data;
      Swal.fire({
        title: `Eliminar ${entityName}`,
        text: MESSAGES.deleteConfirmation(item),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: theme.palette.primary.main,
        cancelButtonText: `No, conservar ${entityName}`,
        confirmButtonText: 'Si, estoy seguro',
        width: '34em',
      }).then(result => {
        if (result.value) {
          del(`${endpoint}/${item._id}`)
            .then(res => handleResponse(res, { history }))
            .then(() => history.push({ pathname: path, state: { msg: 'deleted' } }))
            .catch(err => {
              swal500(err);
            });
        }
      });
    };
  
  return (
    <FormLayout
      handleClose={handleClose}
      loading={loading}
      editMode={editMode}
      submitting={submitting}
      handleSubmit={handleResult}
      handleCancel={handleCancel}
      handleEdit={() => setEditMode(true)}
      handleDelete={handleDelete}
      isNew={isNew}
      entityName={entityName}
      hasChanges={hasChanges}
      copyCurl= {handleCopyCurl}
      enableShare= {true}
    >
      <TextInput
        name="documentType"
        error={errors.documentType}
        value={values.documentType}
        handleChange={handleChange}
        handleBlur={handleBlur}
        label={FIELDS['documentType']}
        disabled={!editMode}
      />
      <TextInput
        name="description"
        error={errors.description}
        value={values.description}
        handleChange={handleChange}
        handleBlur={handleBlur}
        label={FIELDS['description']}
        disabled={!editMode}
      />
      <TextInput
        name="code"
        error={errors.code}
        value={values.code}
        handleChange={handleChange}
        handleBlur={handleBlur}
        label={FIELDS['code']}
        disabled={!editMode}
      />
      <TextInput
        name="alias"
        error={errors.alias}
        value={values.alias}
        handleChange={handleChange}
        handleBlur={handleBlur}
        label={FIELDS['alias']}
        disabled={!editMode}
      />
      <TextInput
        name="identifier"
        error={errors.identifier}
        value={values.identifier}
        handleChange={handleChange}
        handleBlur={handleBlur}
        label={FIELDS['identifier']}
        disabled={!editMode}
      />
      <TextInput
        name="aggregator"
        error={errors.aggregator}
        value={values.aggregator}
        handleChange={handleChange}
        handleBlur={handleBlur}
        label={FIELDS['aggregator']}
        disabled={!editMode}
      />
          <Typography variant='body2' style={{color:'grey', paddingTop:'10px'}}>Esquema</Typography>
          <JsonEditorContainer
            json={values.schema}
            onChangeText={handleSchemaChange}
            editMode={editMode}
          />
      {editMode && <div className={classes.divider} />}
    </FormLayout>
  );
};

export default withStyles(styles)(DocumentTypeForm);
